import { Configuration, PopupRequest } from "@azure/msal-browser";

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

/**
 * Azure AD configuration fetched from backend
 * This is populated by fetchAzureAdConfig() before MSAL initialization
 */
let azureAdConfig: { clientId: string; tenantId: string } | null = null;

/**
 * Fetch Azure AD configuration from backend
 * This must be called before initializing MSAL
 * @returns Promise that resolves when config is fetched
 */
export async function fetchAzureAdConfig(): Promise<void> {
  try {
    console.log("[MSAL CONFIG] Fetching Azure AD config from backend...");
    const response = await fetch("/api/config/azure-ad");

    if (!response.ok) {
      throw new Error(
        `Failed to fetch Azure AD config: ${response.status} ${response.statusText}`
      );
    }

    azureAdConfig = await response.json();

    console.log("[MSAL CONFIG] Azure AD config fetched successfully:", {
      clientId: azureAdConfig?.clientId,
      tenantId: azureAdConfig?.tenantId,
    });

    if (!azureAdConfig?.clientId) {
      throw new Error("ClientId is missing from Azure AD config");
    }

    if (!azureAdConfig?.tenantId) {
      throw new Error("TenantId is missing from Azure AD config");
    }
  } catch (error) {
    console.error("[MSAL CONFIG] Failed to fetch Azure AD config:", error);
    throw error;
  }
}

/**
 * Get MSAL configuration
 * This function should only be called after fetchAzureAdConfig() has completed
 * @returns MSAL Configuration object
 */
export function getMsalConfig(): Configuration {
  if (!azureAdConfig) {
    throw new Error(
      "Azure AD config not loaded. Call fetchAzureAdConfig() first."
    );
  }

  // Single-tenant configuration for "Adappt Testing Azure" tenant
  // All users (including WHO users) must be invited as guest users (B2B) to this tenant
  // All users must be pre-registered in the application database
  const authority = `https://login.microsoftonline.com/${azureAdConfig.tenantId}`;

  return {
    auth: {
      clientId: azureAdConfig.clientId, // Azure AD Application (client) ID
      authority: authority, // Single-tenant endpoint for "Adappt Testing Azure"
      redirectUri: window.location.origin, // Must match Azure AD app registration redirect URI
      postLogoutRedirectUri: window.location.origin, // Redirect after logout
      navigateToLoginRequestUrl: true, // Navigate to original request URL after login
    },
    cache: {
      cacheLocation: "localStorage", // Store tokens in localStorage (survives page refresh)
      storeAuthStateInCookie: false, // Set to true for IE11 or Edge legacy support
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case 0: // Error
              console.error(message);
              return;
            case 1: // Warning
              console.warn(message);
              return;
            case 2: // Info
              console.info(message);
              return;
            case 3: // Verbose
              console.debug(message);
              return;
          }
        },
        logLevel: import.meta.env.DEV ? 2 : 1, // Info in dev, Warning in prod
      },
      allowNativeBroker: false, // Disables WAM Broker (not needed for web apps)
    },
  };
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 *
 * IMPORTANT: For B2B guest users with single-tenant configuration:
 * - Guest users must authenticate through the "Adappt Testing Azure" tenant
 * - Using "select_account" prompt allows users to pick their account
 * - The tenant ID in authority ensures authentication happens in the correct tenant
 * - Guest users will see their guest account (e.g., john.doe_who.int#EXT#@adappt.onmicrosoft.com)
 */
export const loginRequest: PopupRequest = {
  scopes: ["User.Read"], // Microsoft Graph API scope to read user profile
  prompt: "select_account", // Force account selection on every login
  // For B2B: Users should select their GUEST account in "Adappt Testing Azure" tenant
  // NOT their home tenant account (e.g., @who.int)
};

/**
 * Scopes for accessing Microsoft Graph API
 */
export const graphConfig = {
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
};
